/* eslint-disable no-empty */
import config from '@/config';
import { chatAPI } from '@/libs/api';
import { useMounted, useScroll } from '@/libs/hooks';
import { PusherService } from '@/services';
import { chatStore, useAppStore, useChatStore } from '@/store';
import { isEnterKey } from '@/utils/helpers';
import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';

type IsLoader = 'SEND' | 'FILE' | 'NONE';

type MessageType = 'quick_reply' | 'postback';

export const useConversation = () => {
  const [inputValue, setInputValue] = useState('');
  const [fileURL, setFileURL] = useState('');
  const [isLoading, setLoading] = useState<IsLoader>('NONE');

  const unsubPostRef = useRef<() => void>();
  const unsubReplyRef = useRef<() => void>();
  // const unsubEndRef = useRef<() => void>();
  const { elementRef, scrollToBottom } = useScroll<HTMLDivElement>();
  const isMounted = useMounted();

  const { lastMsgID, messages } = useChatStore();
  const {
    userSession: {
      user: { channel, session_id },
    },
    isInteracted,
  } = useAppStore();

  // Audio for incoming message
  const incomingAudio = useCallback(() => {
    const audio = new Audio('/assets/audio/incoming.mp3');
    audio.play();
  }, []);

  // Get messages based on last message id
  const getOldMessages = useCallback(
    async (type?: 'POST' | 'REPLY') => {
      setLoading('SEND');

      try {
        const res = await chatAPI.getOldMessages({
          from_message_id: lastMsgID,
          // case_id: lastMsgCaseID,
        });

        if (res.success && res.data.data) {
          chatStore.setMessages(res.data.data);
          scrollToBottom();
          if (isInteracted && type === 'REPLY') {
            incomingAudio();
          }
          return true;
        }

        return false;
      } catch (error) {
        return false;
      } finally {
        if (isMounted()) {
          setLoading('NONE');
        }
      }
    },
    [lastMsgID, scrollToBottom, isInteracted, incomingAudio, isMounted],
  );

  // Implement pusher service
  useEffect(() => {
    const pusherService = new PusherService(config.broadcastDriver, config.pusherKey);

    if (channel) {
      // user channel name
      // pusherService.subscribe(`messages-user-${mevrik_id}`);
      pusherService.subscribe(`session${session_id}`);
      unsubPostRef.current = pusherService.on(`message_post`, () => getOldMessages('POST'));
      unsubReplyRef.current = pusherService.on(`message_reply`, () => getOldMessages('REPLY'));
      // unsubRef.current = pusherService.on(`message_clsoed`, () => getOldMessages());
      // unsubEndRef.current = pusherService.on(`case_closed`, () => {
      //   pusherService.unsubscribe(channel);
      //   endConversation();
      // });
    }

    return () => {
      pusherService.unsubscribe(channel);
      unsubPostRef.current?.();
      unsubReplyRef.current?.();
      // unsubEndRef.current?.();
    };
  }, [channel, getOldMessages, session_id]);

  // Send message
  const handleSend = useCallback(
    async (messageVal?: string) => {
      const message = messageVal?.trim() || inputValue.trim();
      if (!message && !fileURL) return;

      const payload: IAPI.ImagePayload['message'] = {
        type: 'message',
      };

      if (message) {
        payload.body = message;
      }

      if (fileURL) {
        payload.attachment = {
          type: 'image',
          payload: {
            url: fileURL,
          },
        };
      }

      setLoading('SEND');
      setInputValue('');
      setFileURL('');

      try {
        const res = await chatAPI.sendMessage({
          message: payload,
        });

        console.log(res.data);
        // if (res.success && !lastMsgID) {
        if (res.success) {
          chatStore.setLastMsgID(res.data.message.metadata.id);
          chatStore.setLastCaseID(res.data.message.metadata.case_id);
          getOldMessages('POST');
        }
      } catch (error) {
      } finally {
        if (isMounted()) {
          setLoading('NONE');
        }
      }
    },
    [fileURL, inputValue, isMounted, getOldMessages],
  );

  // Initiate session conversation
  const initSessionConversation = useCallback(async () => {
    setLoading('SEND');

    try {
      const isHistory = await getOldMessages();
      if (!isHistory) {
        await handleSend('Get started');
      }
    } catch (error) {
    } finally {
      if (isMounted()) {
        setLoading('NONE');
      }
    }
  }, [getOldMessages, handleSend, isMounted]);

  useEffect(() => {
    if (!messages.length) {
      initSessionConversation();
    }
  }, [initSessionConversation, messages]);

  const handleChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  }, []);

  // Send message on enter
  const handleKeyPress = useCallback(
    async (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (isEnterKey(e)) {
        e.preventDefault();
        await handleSend();
      }
    },
    [handleSend],
  );

  // Update file
  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files?.length) return;

      const file = e.target.files[0];
      if (!file) return;
      setFileURL(URL.createObjectURL(file));

      setLoading('FILE');
      try {
        const res = await chatAPI.uploadFile(file);
        if (res.success && isMounted()) {
          setFileURL(res.data.url);
          e.target.value = '';
        }
      } catch (error) {
      } finally {
        if (isMounted()) {
          setLoading('NONE');
        }
      }
    },
    [isMounted],
  );

  // Send message on Quick Reply & Postback
  const handleQuickReply = useCallback(
    async (payload: string, title: string, type: MessageType = 'quick_reply') => {
      const params: IAPI.QRPayload = {
        action: 'message_post',
        message: {
          message_type: type,
          body: title,
        },
      };

      if (type === 'quick_reply') {
        params.message.quick_reply = {
          payload,
        };
      }

      if (type === 'postback') {
        params.message.postback = {
          payload,
        };
      }

      if (!params.message.quick_reply && !params.message.postback) return;

      setLoading('SEND');

      try {
        await chatAPI.sendWithQR(params);
        setTimeout(() => getOldMessages('POST'), 2000);
      } catch (error) {
      } finally {
        if (isMounted()) {
          setLoading('NONE');
        }
      }
    },
    [isMounted, getOldMessages],
  );

  // Send message for Generic Template
  const handleGenericTemplate = useCallback(
    async ({ title, type, url, payload }: IAPI.GenericButton) => {
      if (!type) return;

      switch (type) {
        case 'web_url':
          if (!url) return;
          window.open(url, '_blank');
          break;

        case 'phone_number':
          if (!payload) return;
          window.open(`tel:${payload}`);
          break;

        case 'postback':
          if (!title || !payload) return;
          await handleQuickReply(payload, title, 'postback');
          break;

        default:
          break;
      }
    },
    [handleQuickReply],
  );

  return {
    isLoading,
    inputValue,
    fileURL,
    setFileURL,
    scrollerRef: elementRef,
    handleSend,
    handleChange,
    handleKeyPress,
    handleFileChange,
    handleQuickReply,
    handleGenericTemplate,
  };
};
